<template>
  <div class="modal-wrapper">
    <div class="modal-header" v-if="title">{{ title }}</div>
    <div class="modal-content">
      <div class="form-box">
        <el-date-picker
          size="mini"
          v-model="from"
          type="date"
          :value-format="'yyyy-MM-dd'"
          placeholder="From"
        />
        <el-date-picker
          size="mini"
          v-model="to"
          type="date"
          :value-format="'yyyy-MM-dd'"
          placeholder="To"
        />
        <el-button size="mini" type="primary" @click="loadData">
          Search
        </el-button>
      </div>
      <div v-if="performances.length > 0" class="flex flex-col gap-1">
        <div v-for="(group, hub) in performanceByHub" :key="hub">
          <table
            :id="hub"
            class="gray data-grid driver-performance"
            data-filter="true"
            data-export="CSV"
            style="max-height: calc(100vh - 190px)"
          >
            <caption>{{ hub }}</caption>
            <thead>
              <tr>
                <th width="30"></th>
                <th width="150">Driver</th>
                <th data-formatter="rich" width="80">Hired</th>
                <th data-formatter="rich" data-type="number" width="70">
                  Review
                </th>
                <th data-formatter="rich" data-type="number" width="70">
                  Points
                </th>
                <th data-type="number" width="75">Charges</th>
                <th data-type="number" width="70">Moves</th>
                <th data-formatter="rich" data-type="number" width="48">IM</th>
                <th data-formatter="rich" data-type="number" width="48">EX</th>
                <th data-formatter="rich" data-type="number" width="48">XT</th>
                <th data-formatter="rich" data-type="number" width="48">TL</th>
                <th data-type="number" width="80">0~100mi</th>
                <th data-type="number" width="90">100~200mi</th>
                <th data-type="number" width="90">200~300mi</th>
                <th data-type="number" width="80">300+mi</th>
                <th data-type="number" width="75">Triaxles</th>
                <th data-formatter="rich" data-type="number" width="65">
                  Lates
                </th>
                <th data-formatter="rich" data-type="number" width="75">
                  Lates%
                </th>
                <th data-type="number" width="90">Delay Mins</th>
                <th data-type="number" width="90">Workdays</th>
                <th data-type="number" width="75">Disp.</th>
                <th data-type="number" width="75">Undisp.</th>
                <th data-type="number" width="80">No Work</th>
                <th data-type="number" width="80">Day Off</th>
                <th data-type="number" width="85">Worked%</th>
                <th data-type="number" width="80">Cancelled</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in group" :key="index">
                <td :data-order="row.trucker.active">
                  <i
                    class="fas fa-check-circle green"
                    v-if="row.trucker.active"
                  >
                    <span class="hidden">✅</span>
                  </i>
                </td>
                <td>{{ row.trucker.name }}</td>
                <td>
                  {{ row.trucker.hire_date }}
                  <div class="text-2xs tracking-tighter">
                    {{ row.trucker.seniority }}
                  </div>
                </td>
                <td class="text-center">
                  <a @click="openReview(row)">{{
                    row.rating | withDelimiter
                  }}</a>
                </td>
                <td class="text-center">
                  <popover
                    componentPath="components/html"
                    width="960"
                    placement="right"
                    :options="{ url: row.points_url, title: row.trucker.name }"
                  >
                    <div class="w-full underline">{{ row.points }}</div>
                  </popover>
                </td>
                <td>{{ row.charges | toCurrency({ max: 0 }) }}</td>
                <td>{{ row.moves }}</td>
                <slot v-for="type in ['import', 'export', 'xtown', 'tl']">
                  <td :key="type">
                    <el-popover
                      v-if="row.orders[type]"
                      :title="row.trucker.name"
                      placement="left"
                      width="600"
                      trigger="click"
                      @show="loadOrderDetails(row, type)"
                    >
                      <div class="order-details">
                        <table
                          class="tbl text-left"
                          v-if="!isEmptyData(row[`${type}Details`])"
                        >
                          <thead>
                            <tr>
                              <th width="250">Destination</th>
                              <th width="60"># Cont.</th>
                              <th>IDs</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="(group, name, index) in row[
                                `${type}Details`
                              ]"
                              :key="index"
                            >
                              <td>{{ name }}</td>
                              <td>{{ group.length }}</td>
                              <td class="text-left stripe">
                                <div class="flex items-center flex-wrap gap-1">
                                  <slot v-for="item in group">
                                    <a
                                      :key="item.id"
                                      @click="openOrder(item.id)"
                                      >{{ item.id }}</a
                                    >
                                  </slot>
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <a class="block" slot="reference">
                        {{ row.orders[type].count }}
                      </a>
                    </el-popover>
                  </td>
                </slot>
                <td>{{ row.lt100mi }}</td>
                <td>{{ row.gteq100lt200mi }}</td>
                <td>{{ row.gteq200lt300mi }}</td>
                <td>{{ row.gteq300mi }}</td>
                <td>{{ row.triaxles }}</td>
                <td :data-order="row.lates.count">
                  <el-popover
                    :title="row.trucker.name"
                    placement="left"
                    width="500"
                    trigger="click"
                    @show="loadLateDetails(row)"
                  >
                    <div class="order-details">
                      <table
                        class="tbl text-left"
                        v-if="!isEmptyData(row.lateDetails)"
                      >
                        <thead>
                          <tr>
                            <th width="250">Destination</th>
                            <th width="60"># Cont.</th>
                            <th>IDs</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="(group, name, index) in row.lateDetails"
                            :key="index"
                          >
                            <td>{{ name }}</td>
                            <td>{{ group.length }}</td>
                            <td class="text-left stripe">
                              <slot v-for="item in group">
                                <div
                                  :key="item.id"
                                  class="flex-space stripe-item"
                                >
                                  <a @click="openOrder(item.id)">{{
                                    item.id
                                  }}</a>
                                  <div>{{ item.delay }}</div>
                                </div>
                              </slot>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <a class="block" slot="reference">{{ row.lates.count }}</a>
                  </el-popover>
                </td>
                <td>
                  <a class="block" @click="openUrl(row.lates.url)">
                    {{ row.lates.ratio | toPercentage }}
                  </a>
                </td>
                <td>{{ row.lates.mins }}</td>
                <td>{{ row.workdays }}</td>
                <td>{{ row.dispatched }}</td>
                <td>{{ row.undispatched }}</td>
                <td>{{ row.no_works }}</td>
                <td>{{ row.day_offs }}</td>
                <td>{{ row.worked_ratio | toPercentage }}</td>
                <td>{{ row.cancels }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import DataGrid from "../../../controllers/data_grid";

export default {
  name: "DriverPerformance",
  props: {
    title: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      from: null,
      to: null,
      performances: [],
    };
  },
  mounted() {
    this.loadData();
  },
  watch: {
    date(nv, ov) {
      if (nv != ov) {
        this.loadData(nv);
      }
    },
  },
  computed: {
    performanceByHub() {
      return this._.groupBy(this.performances, "trucker.hub");
    },
  },
  methods: {
    seniority(ymd) {
      return new Date() - new Date(ymd);
    },
    dateRange() {
      return { from: this.from, to: this.to };
    },
    loadData() {
      this.performances = [];
      this.$http
        .get("/api/v1/drivers/performance", {
          params: this.dateRange(),
        })
        .then((response) => {
          const data = response.data;
          this.from = data.from;
          this.to = data.to;
          this.performances = data.performances;
          setTimeout(() => DataGrid.init(), 250);
        })
        .catch((err) => {});
    },
    reviewOptions(item) {
      return {
        driver: item.trucker,
        callback: () => {
          this.loadData();
        },
      };
    },
    async loadOrderDetails(row, type) {
      try {
        if (row[`loaded${type}Details`]) return;
        const ids = row.orders[type].ids;
        const response = await this.$http.post(
          "/api/v1/containers/destination",
          { ids },
        );
        row[`loaded${type}Details`] = true;
        this.$set(row, `${type}Details`, response.data);
      } catch (ex) {
        console.log(ex);
      }
    },
    loadLateDetails(row) {
      if (!row.loadedLateDetails) {
        const ids = row.lates.orders.map((o) => o.id);
        this.$http
          .post("/api/v1/containers/destination", { ids })
          .then((response) => {
            let data = response.data;
            let items = Object.values(data).flat();
            row.lates.orders.forEach((o) => {
              let item = items.find((item) => item.id == o.id);
              if (item) item.delay = o.delay;
            });
            row.loadedLateDetails = true;
            this.$set(row, "lateDetails", data);
          })
          .catch((err) => {
            console.log(err);
          });
      }
    },
    openReview(row) {
      Dialog.open({
        componentPath: "pages/drivers/review",
        width: "960px",
        height: "600px",
        ...this.reviewOptions(row),
      });
    },
    openOrder(id) {
      const options = this.viewOrderOptions(id);
      if (options.disabled) {
        this.$message({
          message: options.disabled.warning,
          type: "warning",
        });
        return;
      }
      Dialog.open({
        width: "900px",
        clickToClose: true,
        pivotY: 0,
        ...options,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.order-details {
  @apply max-h-96 overflow-auto border;
}
.driver-performance::v-deep {
  .tabulator-col-title {
    @apply text-2xs #{!important};
  }
}
</style>
